import React, { Dispatch, useRef } from 'react';
import Icon from '@mdi/react';
import { Box, Typography } from '@mui/material';
import DynamicTreeItem, { labelTextStyles } from '../../General/DynamicTree/DynamicTreeItem';
import { bodyFont, vars } from '../../../Theme';
import TranslatedCategoryName from '../TranslatedCategoryName/TranslatedCategoryName';
import { useDispatch, useSelector } from 'react-redux';
import { IAppReduxStore } from '../../../redux/store';
import { setDndActive } from '../../../redux/general/sliceDragDrop';
import { TreeItemProps } from '@mui/x-tree-view';

/**
 * TreeViewItem that shows a category in a TreeView.
 */
export const DynamicTreeItemCategory = (props: ITreeItemCategoryProps) => {
    const dispatch: Dispatch<unknown> = useDispatch();
    const treeItem = useRef<HTMLLIElement>();
    const { dndActive, dndIntent } = useSelector((_store: IAppReduxStore) => _store.dragDrop);

    /** Deconstruct props. */
    const {
        itemId,
        labelIcon,
        labelText,
        compact,
        active,
        count,
        paddingLeft,
        onDropDocuments,
        forceSelectionChangeFn,
        forceExpandedChangeFn,
        isSkeleton,
        disableTransition,
        isExpandedAll,
        /** All other values are stored in 'other'. */
        ...other
    } = props;

    /** Label Component. */
    const itemLabel = (
        <Box sx={{ display: 'flex', alignItems: 'center', p: 0 }}>
            {labelIcon && (
                <Box
                    sx={{
                        color: 'inherit',
                    }}
                >
                    <Icon path={labelIcon} size={1} />
                </Box>
            )}
            <Typography variant='body2' sx={labelTextStyles(+!compact)}>
                <TranslatedCategoryName categoryName={labelText} />
            </Typography>
            <Typography
                variant='caption'
                sx={{ ...bodyFont, color: vars.concrete, fontSize: 12, px: 1 }}
            >
                {count > 0 ? `(${count.toString()})` : ''}
            </Typography>
        </Box>
    );

    const onMouseEnter = (ev: React.MouseEvent<HTMLLIElement>) => {
        if (dndIntent !== 'documentCategorization') {
            return;
        }

        ev.stopPropagation(); // no propagation to parent

        const contentEl = treeItem.current?.children?.[0] as HTMLDivElement;
        if (contentEl && !contentEl.classList.contains('Mui-selected')) {
            if (isSkeleton) {
                forceExpandedChangeFn?.(itemId, true); // expand only
            } else if (!contentEl.classList.contains('Mui-expanded')) {
                contentEl.click(); // select + expand
            } else {
                forceSelectionChangeFn?.(itemId, true); // select only
            }
        }
    };

    const onMouseLeave = (ev: React.MouseEvent<HTMLLIElement>) => {
        if (dndIntent !== 'documentCategorization') {
            return;
        }

        const leavedEl = ev.currentTarget as HTMLElement;
        const enteredEl = ev.relatedTarget as HTMLElement;

        if (!leavedEl || !enteredEl) {
            return;
        }

        if (enteredEl.getAttribute('class') === 'importTableDragPreview') {
            return;
        }

        if (treeItem.current.contains(enteredEl)) {
            return;
        }

        forceSelectionChangeFn?.(itemId, false);

        const contentEl = leavedEl.children?.[0] as HTMLElement;
        if (contentEl && contentEl.classList.contains('Mui-expanded')) {
            forceExpandedChangeFn?.(itemId, false);
        }

        setTimeout(() => {
            if (contentEl && contentEl.classList.contains('Mui-focused')) {
                contentEl.classList.remove('Mui-focused');
            }
        }, 25);
    };

    const onMouseUp = (ev: React.MouseEvent<HTMLLIElement>) => {
        if (dndIntent !== 'documentCategorization') {
            return;
        }

        ev.stopPropagation(); // no propagation to parent

        dispatch(setDndActive(false));
        onDropDocuments?.(itemId);
    };

    return (
        <DynamicTreeItem
            ref={treeItem}
            itemId={itemId}
            label={itemLabel}
            paddingLeft={paddingLeft}
            disableTransition={disableTransition}
            isExpandedAll={isExpandedAll}
            {...other}
            onMouseEnter={dndActive ? onMouseEnter : undefined}
            onMouseLeave={dndActive ? onMouseLeave : undefined}
            onMouseUp={dndActive ? onMouseUp : undefined}
        />
    );
};

/**
 * Interface that describes the properties for TreeItemCategory components.
 */
export interface ITreeItemCategoryProps extends TreeItemProps {
    /** TreeItem child node count. */
    count: number;

    /** TreeItem title text. */
    labelText: string;

    /** If TreeItem is active or not. */
    active: boolean;

    /** (Optional) TreeItem Icon. */
    labelIcon?: string;

    /** (Optional) Display type. */
    compact?: boolean;

    /** (Optional) Padding left. */
    paddingLeft?: number;

    /** (Optional) Drop handler. */
    onDropDocuments?: (itemId: string) => void;

    /** (Optional) Function to externally select/deselect. */
    forceSelectionChangeFn?: (itemId: string, state: boolean) => void;

    /** (Optional) Function to externally expand/collapse. */
    forceExpandedChangeFn?: (itemId: string, state: boolean) => void;

    isSkeleton?: boolean;

    disableTransition?: boolean;

    isExpandedAll?: boolean;
}
